// Risk Calculation Utilities

// Type guard to ensure a value is a number
function ensureNumber(value: unknown, defaultValue: number = 0): number {
  if (typeof value === 'number' && !isNaN(value)) {
    return value
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value)
    return !isNaN(parsed) ? parsed : defaultValue
  }
  return defaultValue
}

// Safe property access with type guards
function getPropertyValue(property: any, field: string, defaultValue: number = 0): number {
  return ensureNumber(property?.[field], defaultValue)
}

export function calculatePortfolioConcentration(properties: any[]): number {
  if (!properties || properties.length === 0) return 0

  // Calculate geographic concentration
  const locationCounts = properties.reduce((acc, property) => {
    const key = `${property.city}, ${property.state}`.toLowerCase()
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const totalProperties = properties.length
  const maxConcentration = Math.max(...Object.values(locationCounts).map(val => ensureNumber(val)))

  // Higher percentage in one location = higher risk score
  return Math.round((maxConcentration / totalProperties) * 100)
}

export function calculateLeverageRisk(properties: any[]): number {
  if (!properties || properties.length === 0) return 0

  const totalValue = properties.reduce((sum, p) => sum + getPropertyValue(p, 'purchase_price'), 0)
  const totalDebt = properties.reduce((sum, p) => sum + getPropertyValue(p, 'loan_amount'), 0)

  return totalValue > 0 ? Math.round((totalDebt / totalValue) * 100) : 0
}

export function calculateCashFlowStability(properties: any[]): number {
  if (!properties || properties.length === 0) return 0

  // Mock calculation - in real implementation, you'd analyze historical cash flows
  const avgCashFlow = properties.reduce((sum, p) => {
    const loanAmount = getPropertyValue(p, 'loan_amount')
    const interestRate = getPropertyValue(p, 'interest_rate')
    const loanTerm = getPropertyValue(p, 'loan_term', 30)

    const monthlyMortgage = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)
    const monthlyExpenses = monthlyMortgage + (getPropertyValue(p, 'annual_taxes') / 12) + (getPropertyValue(p, 'annual_insurance') / 12)
    const netCashFlow = getPropertyValue(p, 'expected_monthly_rent') - monthlyExpenses
    return sum + netCashFlow
  }, 0) / properties.length

  // Convert to stability score (0-100, higher is more stable)
  return Math.max(0, Math.min(100, Math.round(50 + (avgCashFlow / 100))))
}

export function calculateMonthlyPayment(loanAmount: number, annualInterestRate: number, loanTermYears: number): number {
  const monthlyInterestRate = annualInterestRate / 100 / 12
  const numberOfPayments = loanTermYears * 12

  if (monthlyInterestRate === 0) {
    return loanAmount / numberOfPayments
  }

  const monthlyPayment = loanAmount *
    (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
    (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)

  return Math.round(monthlyPayment)
}

export function generateMockRiskData(properties: any[]) {
  // Generate mock risk assessment data for demonstration
  // In production, this would come from the backend API

  const concentrationScore = calculatePortfolioConcentration(properties)
  const leverageRatio = calculateLeverageRisk(properties)
  const liquidityScore = Math.round(Math.random() * 40 + 40) // 40-80
  const vacancyRiskScore = Math.round(Math.random() * 30 + 10) // 10-40
  const marketCorrelation = Math.random() * 0.6 + 0.2 // 0.2-0.8

  const overallRiskScore = Math.round(
    (concentrationScore * 0.3) +
    (leverageRatio * 0.25) +
    ((100 - liquidityScore) * 0.2) +
    (vacancyRiskScore * 0.15) +
    (marketCorrelation * 100 * 0.1)
  )

  return {
    overall_risk_score: Math.max(0, Math.min(100, overallRiskScore)),
    concentration_risk: {
      geographic: {
        locations: generateMockLocationData(properties),
        concentration_score: concentrationScore,
        diversification_recommendation: getConcentrationRecommendation(concentrationScore)
      },
      property_type: {
        types: generateMockPropertyTypeData(properties),
        concentration_score: concentrationScore,
        recommendation: getConcentrationRecommendation(concentrationScore)
      },
      tenant_type: {
        tenant_quality_score: Math.round(Math.random() * 30 + 60),
        high_risk_tenants: Math.round(Math.random() * 3),
        average_lease_length: Math.round(Math.random() * 12 + 12),
        tenant_turnover_rate: Math.round(Math.random() * 20 + 10)
      },
      market_exposure: {
        markets: generateMockMarketData(properties),
        correlation_score: marketCorrelation
      }
    },
    liquidity_score: liquidityScore,
    leverage_ratio: leverageRatio,
    vacancy_risk_score: vacancyRiskScore,
    market_correlation: marketCorrelation,
    stress_test_results: {
      scenarios: [],
      worst_case_loss: Math.round(Math.random() * 100000 + 50000),
      survival_probability: Math.round(Math.random() * 30 + 70),
      recommended_actions: [
        "Reduce geographic concentration",
        "Increase cash reserves",
        "Consider refinancing high-rate loans"
      ]
    }
  }
}

function generateMockLocationData(properties: any[]) {
  const locationGroups = properties.reduce((acc, property) => {
    const key = `${property.city || 'Unknown'}, ${property.state || 'Unknown'}`
    if (!acc[key]) {
      acc[key] = {
        city: property.city || 'Unknown',
        state: property.state || 'Unknown',
        zip_code: property.zip_code || '00000',
        properties: [],
        total_value: 0
      }
    }
    acc[key].properties.push(property)
    acc[key].total_value += getPropertyValue(property, 'purchase_price')
    return acc
  }, {} as any)

  const totalValue = properties.reduce((sum, p) => sum + getPropertyValue(p, 'purchase_price'), 0)

  return Object.values(locationGroups).map((group: any) => ({
    city: group.city,
    state: group.state,
    zip_code: group.zip_code,
    percentage: (group.total_value / totalValue) * 100,
    property_count: group.properties.length,
    total_value: group.total_value,
    risk_level: (group.properties.length > properties.length * 0.4 ? 'high' :
                group.properties.length > properties.length * 0.2 ? 'medium' : 'low') as 'low' | 'medium' | 'high'
  }))
}

function generateMockPropertyTypeData(properties: any[]) {
  const typeGroups = properties.reduce((acc, property) => {
    const type = property.property_type || 'single-family'
    if (!acc[type]) {
      acc[type] = {
        properties: [],
        total_value: 0
      }
    }
    acc[type].properties.push(property)
    acc[type].total_value += getPropertyValue(property, 'purchase_price')
    return acc
  }, {} as any)

  const totalValue = properties.reduce((sum, p) => sum + getPropertyValue(p, 'purchase_price'), 0)

  return Object.entries(typeGroups).map(([type, group]: [string, any]) => ({
    type: type as 'single-family' | 'multi-family' | 'commercial' | 'industrial' | 'land',
    percentage: (group.total_value / totalValue) * 100,
    property_count: group.properties.length,
    total_value: group.total_value,
    risk_level: (group.properties.length > properties.length * 0.6 ? 'high' :
                group.properties.length > properties.length * 0.3 ? 'medium' : 'low') as 'low' | 'medium' | 'high'
  }))
}

function generateMockMarketData(properties: any[]) {
  const markets = Array.from(new Set(properties.map(p => `${p.city}, ${p.state}`)))

  return markets.map(market => ({
    market_name: market,
    exposure_percentage: Math.round(Math.random() * 40 + 10),
    market_health_score: Math.round(Math.random() * 30 + 60),
    growth_trend: (['growing', 'stable', 'declining'][Math.floor(Math.random() * 3)]) as 'growing' | 'stable' | 'declining',
    employment_rate: Math.round((Math.random() * 10 + 85) * 10) / 10,
    population_growth: Math.round((Math.random() * 4 + 1) * 10) / 10
  }))
}

function getConcentrationRecommendation(score: number): string {
  if (score > 70) {
    return "High concentration risk detected. Diversify across multiple markets to reduce portfolio risk."
  } else if (score > 40) {
    return "Moderate concentration. Consider expanding to additional markets for better diversification."
  } else {
    return "Good diversification across markets. Maintain balanced allocation as portfolio grows."
  }
}